import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { BREAKPOINT_TABLET } from "../../../../constants/constants";
import { EOS_REPORT_TIME_DIVISOR } from "../../../../constants/customers";
import {
  getFormattedHoursMinutes,
  getFormattedMinutes,
  getReadableHoursAndMinutes,
  ONE_MINUTE_OF_MS,
} from "reports/utils/timeUtils";
import { PnlTimes } from "../PnLChart/pnlChartTypes";
import { BreakdownType, BreakdownTypeEnum } from "./metricAndPnlBreakdownTypes";
import { BREAKDOWN_ROW_PADDING_EXPANDED_PX } from "./metricAndPnLBreakdownUtils";
import { ReportParams } from "reports/types";
import { useParams } from "react-router";

const MachinePnLChartStyles = styled.div<{ $numCategories: number }>`
  display: grid;
  justify-content: space-between;
  font-size: 15px;
  padding: ${BREAKDOWN_ROW_PADDING_EXPANDED_PX}px
    ${BREAKDOWN_ROW_PADDING_EXPANDED_PX}px
    ${BREAKDOWN_ROW_PADDING_EXPANDED_PX * 1.5}px
    ${BREAKDOWN_ROW_PADDING_EXPANDED_PX}px;
  grid-template-columns: repeat(${props => props.$numCategories + 1}, auto);

  .pnlTitle {
    font-weight: 700;
  }

  .categoryBreakdown {
    display: flex;
    align-items: center;
  }

  .colorCircle {
    width: 10px;
    height: 10px;
    border: 0.5px solid #000000;
    border-radius: 9px;
  }

  .timeAndPercent {
    padding-left: 4px;
  }
  .dash {
    padding-left: 6px;
    margin-top: -2px;
  }
  .spacer {
    display: flex;
    justify-content: center;
  }

  .timeAndPercent {
    display: grid;
    grid-template-columns: auto 4px auto;
    grid-column-gap: 4px;
  }

  @media (max-width: ${BREAKPOINT_TABLET}px) {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 1em 10px 1em;

    .timeAndPercent {
      grid-template-columns: 35px 5px auto;
      grid-column-gap: 10px;
    }
  }
`;

export default function ProcessPnlBreakdown({
  pnlTimes,
  breakdownType = BreakdownTypeEnum.process,
}: {
  pnlTimes: PnlTimes[];
  breakdownType?: BreakdownType;
}) {
  const { t } = useTranslation("EOSReport");
  const { ckey } = useParams() as ReportParams;
  const timeDivisor = EOS_REPORT_TIME_DIVISOR?.[ckey];

  const pnlTitle = `${breakdownType}PnlBreakdown`;

  return (
    <MachinePnLChartStyles $numCategories={pnlTimes.length}>
      <div className="pnlTitle">{t(pnlTitle)}</div>
      {pnlTimes.map(({ name, time, value, color, shouldHideValue }) => {
        const timeInMinutes = getFormattedMinutes(time);
        const { hours, minutes } = getFormattedHoursMinutes(time);

        const readableTime =
          timeDivisor === ONE_MINUTE_OF_MS
            ? `${timeInMinutes} min`
            : getReadableHoursAndMinutes(time ?? 0);

        const isAllZeroes =
          value === 0 && Number(hours) === 0 && Number(minutes) === 0;
        const isValidValue = value && isFinite(value);

        return (
          <div key={name} className="categoryBreakdown">
            <div className="colorCircle" style={{ backgroundColor: color }} />
            {!isValidValue || isAllZeroes ? (
              <div className="dash">--</div>
            ) : (
              <div className="timeAndPercent">
                {!shouldHideValue && (
                  <>
                    <div className="percent">{value}%</div>
                    <div className="spacer">|</div>
                  </>
                )}
                <div className="time">{readableTime}</div>
              </div>
            )}
          </div>
        );
      })}
    </MachinePnLChartStyles>
  );
}
